// Provenance for wrapped components — the "new component" / "drift" signals.
//
// A wrapped component earns one of these reads:
//   new      — prototype-owned (file under front-end/designs/) and llama has no
//              component of this name. A candidate to promote into llama.
//   drift    — prototype-owned AND a component of the same name exists in llama:
//              a fork that may have diverged from (or been superseded by) the
//              real one. Reconcile before relying on it.
//   llama    — no prototype file: consumed from llama / raw MUI. Just usage.
//   scaffold — device/mockup chrome that never ships. Not a signal.
//
// The llama side is a filesystem fact captured by scripts/ds-component-scan.mjs
// into llama-components.json (refresh it in the session-start sweep). The
// classification is done here against the registry.

import llamaComponents from '@/designs-dev/inspect/llama-components.json';
import componentDrift from '@/designs-dev/inspect/component-drift.json';
import { REGISTRY } from '@/designs-dev/inspect/ds-registry';
import { localFileFor } from '@/designs-dev/inspect/local';

const llamaNames = new Set<string>(llamaComponents.names);

type DriftDetail = {
  parsed: boolean;
  propsAdded: string[];
  propsRemoved: string[];
};
const driftDetails = (componentDrift.components ?? {}) as Record<
  string,
  DriftDetail
>;

// Truncate a prop list so a callout stays readable.
const briefList = (props: string[], max = 4): string => {
  if (props.length <= max) return props.map((p) => `\`${p}\``).join(', ');
  return (
    props
      .slice(0, max)
      .map((p) => `\`${p}\``)
      .join(', ') + ` +${props.length - max} more`
  );
};

// What diverged from llama, at the public-props level — appended to the drift
// note when the scan could parse both sides.
const driftDelta = (name: string): string => {
  const d = driftDetails[name];
  if (!d || !d.parsed) return ' (couldn’t diff props — inspect the source).';
  const parts: string[] = [];
  if (d.propsAdded.length) parts.push(`adds ${briefList(d.propsAdded)}`);
  if (d.propsRemoved.length) parts.push(`drops ${briefList(d.propsRemoved)}`);
  if (parts.length === 0) {
    return ' Same public props as llama — implementation may still differ.';
  }
  return ` Prototype ${parts.join('; ')}.`;
};

// Device/mockup scaffolding — the phone/laptop chrome. Never shipped, so it's
// neither new nor drift.
const SCAFFOLD = new Set([
  'PhoneFrame',
  'StatusBar',
  'HomeIndicator',
  'MobileKeyboard',
  'ComputerFrame',
]);

const isPrototypeFile = (file?: string): boolean =>
  !!file && file.startsWith('front-end/designs/');

export type ComponentProvenance = {
  kind: 'new' | 'drift' | 'llama' | 'scaffold';
  note: string;
};

// Product areas of the designs app. A wrapped component whose file lives under
// one of these is a FEATURE component (composition scoped to a feature/screen),
// as opposed to a reusable design-system component (which lives in components/).
const PRODUCT_AREAS = [
  'karehero-app',
  'cam-admin',
  'james-playground',
  'referring-partner-portal',
];

export type ComponentRole = 'feature' | 'component' | 'layout';

// A wrapped component's role, derived from where it lives — no stored flag, so
// it can't rot:
//   layout    — reusable shell/structure of a screen (sidebar, body, content
//               area). Lives in a layout/ directory. Owns its layout via system
//               props and composes children through; not feature-specific.
//   feature   — composition scoped to one feature/screen (file under a product
//               area). Owns its layout and composes DS components + primitives;
//               it graduates into the design system via its handover, not alone.
//   component — a reusable design-system component (file under components/).
// Null for non-wrapped, scaffold, or anything outside those locations (e.g.
// designs-dev/ device chrome, or a component consumed from llama).
export const componentRole = (name: string): ComponentRole | null => {
  const entry = REGISTRY[name];
  // A pipe or theme component has no role; one with no card at all is judged by
  // where its file is, the same way a wrapped one is.
  if (entry && entry.approach !== 'wrapped') return null;
  if (SCAFFOLD.has(name)) return null;
  const file = entry?.file ?? localFileFor(name);
  if (!file || !isPrototypeFile(file)) return null;
  if (file.includes('/layout/')) return 'layout';
  if (file.startsWith('front-end/designs/src/components/')) return 'component';
  if (PRODUCT_AREAS.some((a) => file.startsWith(`front-end/designs/src/${a}/`)))
    return 'feature';
  return null;
};

// Classify a registry component by name (use baseName() on the inspection label
// first). Returns null for non-wrapped components — pipe/theme components have no
// provenance to flag.
export const classifyComponent = (name: string): ComponentProvenance | null => {
  const entry = REGISTRY[name];
  if (entry && entry.approach !== 'wrapped') return null;
  // No card is not the same as no component: the prototypes may own it and
  // nobody have written it up. Provenance is a fact about the file, so it can be
  // told either way.
  const file = entry?.file ?? localFileFor(name);
  if (!entry && !file) return null;

  if (SCAFFOLD.has(name)) {
    return { kind: 'scaffold', note: 'Device/mockup chrome — never shipped.' };
  }
  if (!isPrototypeFile(file)) {
    return {
      kind: 'llama',
      note: 'Consumed from llama / raw MUI — not a prototype-owned component.',
    };
  }
  if (llamaNames.has(name)) {
    return {
      kind: 'drift',
      note:
        `Forks the llama ${name} (prototype owns ${file}).` +
        driftDelta(name) +
        ` Reconcile before relying on it.`,
    };
  }
  return {
    kind: 'new',
    note:
      `New prototype component — llama has no ${name} (${file}). ` +
      `A candidate to promote into llama once it settles.`,
  };
};
